import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Delete,
  CloudUpload,
  Add,
  Link as LinkIcon,
} from "@mui/icons-material";
import { useState } from "react";

const FileUploadPreview = ({
  label,
  id,
  fileField = "file",
  acceptedTypes = "",
  onFileChange,
  allowUrl = true,
  urlField = "url",
  getFieldValue,
  onUrlChange,
  onRemove,
  onRemoveItem,
  onAddUrl,
  error,
  errors = {},
  multiple = false,
  urlLabel,
}) => {
  const [urlInput, setUrlInput] = useState("");

  const files = getFieldValue(fileField);
  const url = getFieldValue(urlField);

  let sources = [];
  let fileObjects = [];

  if (multiple) {
    if (files instanceof Array) {
      files.forEach((file) => {
        if (file instanceof File) {
          sources.push(URL.createObjectURL(file));
          fileObjects.push(file);
        } else if (file && file.isDraftPlaceholder) {
          sources.push("");
          fileObjects.push(file);
        } else if (typeof file === "string") {
          sources.push(file);
          fileObjects.push(file);
        }
      });
    }
    if (url instanceof Array) {
      sources.push(...url);
      fileObjects.push(...url);
    } else if (url) {
      sources.push(url);
      fileObjects.push(url);
    }
  } else {
    let singleSrc = null;
    let singleFile = null;
    if (files instanceof File) {
      singleSrc = URL.createObjectURL(files);
      singleFile = files;
    } else if (files && files.isDraftPlaceholder) {
      singleSrc = "";
      singleFile = files;
    } else if (files && typeof files === "string") {
      singleSrc = files;
      singleFile = files;
    } else if (allowUrl && url) {
      singleSrc = url;
      singleFile = url;
    }
    if (singleFile !== null) {
      sources = [singleSrc];
      fileObjects = [singleFile];
    }
  }

  const handleFileChange = (event) => {
    onFileChange(event);
    if (onUrlChange && !multiple) {
      onUrlChange({ target: { name: urlField, value: "" } });
    }
  };

  const handleRemoveClick = (index) => {
    if (multiple) {
      if (onRemoveItem) onRemoveItem(index);
    } else {
      if (onRemove) onRemove();
      else if (onFileChange)
        onFileChange({ target: { name: fileField, value: null } });
      if (onUrlChange) onUrlChange({ target: { name: urlField, value: null } });
    }
  };

  const handleAddUrlClick = () => {
    if (onAddUrl && urlInput) {
      onAddUrl(urlInput);
      setUrlInput("");
    }
  };

  return (
    <Box className="file-upload-container">
      {sources.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
          {sources.map((src, index) => {
            const file = fileObjects[index];
            const isDraftPlaceholder = file && file.isDraftPlaceholder;
            const isVideo = src && /\.(mp4|webm|ogg)$/i.test(src);
            const isPdf =
              (file instanceof File && file.type === "application/pdf") ||
              (isDraftPlaceholder && file.name?.toLowerCase().endsWith(".pdf")) ||
              (typeof src === "string" && src.endsWith(".pdf"));
            const isGenericFile =
              file instanceof File &&
              file.type &&
              !file.type.startsWith("image/") &&
              !isPdf &&
              !isVideo;

            return (
              <Box
                key={index}
                className="preview-container"
                position="relative"
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  overflow: "hidden",
                  width: 150,
                  height: 150,
                }}
              >
                {isDraftPlaceholder ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1.5,
                      textAlign: "center",
                      backgroundColor: "#fef8f0",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: "100%",
                        fontWeight: "500",
                        color: "#333",
                        fontSize: "0.85rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                      title={file.name}
                    >
                      {file.name || "Draft File"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ mt: 1, fontSize: "0.75rem", fontWeight: "bold" }}
                    >
                      [Draft: Re-upload]
                    </Typography>
                  </Box>
                ) : isVideo ? (
                  <video
                    src={src}
                    controls
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : isPdf ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                    }}
                  >
                    <Typography variant="body2" noWrap title={file.name}>
                      {file.name || "PDF Document"}
                    </Typography>
                    <Button href={src} target="_blank" size="small">
                      View PDF
                    </Button>
                  </Box>
                ) : isGenericFile ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                    }}
                  >
                    <Typography variant="body2" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Button href={src} target="_blank" size="small">
                      Download
                    </Button>
                  </Box>
                ) : (
                  <img
                    src={src}
                    alt={`${label} preview ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}

                {/* Individual Remove Button */}
                <IconButton
                  onClick={() => handleRemoveClick(index)}
                  className="remove-media"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    background: "rgba(255,255,255,0.7)",
                    "&:hover": { background: "rgba(255,255,255,0.9)" },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      )}
      <Stack direction="row" gap={2} flexWrap="wrap" mt={2}>
        <input
          accept={acceptedTypes}
          type="file"
          name={fileField}
          id={id}
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple={multiple}
        />
        <label
          htmlFor={id}
          style={{ flex: 1, minWidth: "200px", margin: "8px 0" }}
        >
          <Button
            variant="outlined"
            component="span"
            startIcon={multiple ? <Add /> : <CloudUpload />}
            fullWidth
            sx={{ height: "100%" }}
          >
            {multiple ? `Add ${label} File` : `Upload ${label}`}
          </Button>
        </label>
        {allowUrl && (
          <Chip style={{ margin: "auto", fontSize: "1rem" }} label="OR" />
        )}
        {allowUrl && multiple && (
          <Stack
            direction="row"
            gap={1}
            style={{ flex: 2, minWidth: "300px", margin: "8px 0" }}
          >
            <TextField
              label={`Add ${label} URL`}
              variant="outlined"
              style={{ flex: 1 }}
              value={urlInput}
              placeholder={`Enter ${label} URL`}
              onChange={(e) => setUrlInput(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleAddUrlClick}
              startIcon={<LinkIcon />}
              sx={{ height: "56px" }}
            >
              Add URL
            </Button>
          </Stack>
        )}
        {allowUrl && !multiple && (
          <TextField
            label={`${label} URL`}
            variant="outlined"
            style={{ flex: 1, minWidth: "200px", margin: "8px 0px" }}
            value={url || ""}
            name={urlField}
            placeholder={`Enter ${label} URL`}
            onChange={onUrlChange}
            error={!!(errors && errors[urlField])}
            helperText={errors && errors[urlField]}
          />
        )}
      </Stack>
      {(error || (errors && (errors[fileField] || errors[urlField]))) && (
        <Typography
          color="error"
          variant="caption"
          sx={{ mt: 1, display: "block" }}
        >
          {error || errors[fileField] || errors[urlField]}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploadPreview;
